import { Flex, Title, Transition, Box, Badge } from '@mantine/core';
import React, { useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useHoldingDetailsController } from './useHoldingDetailsController';
import { PresentAsSwitch } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingDetails/PresentAsSwitch.tsx';
import { DateSelectionMenu } from '@dealstack/frontend/app/pages/Project/Cap/common/DateSelectMenu/dateSelectMenu.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { CapInvalidTransactionsWarning } from '@dealstack/frontend/shared/components/CapInvalidTransactionsWarning/CapInvalidTransactionsWarning.tsx';
import { HoldingDetailsSummary } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingDetails/HoldingDetailsSummary.tsx';
import { HoldingEventsLogTable } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingDetails/HoldingEventsLogTable.tsx';
import { HoldingEventsLogTimeline } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingDetails/HoldingEventsLogTimeline.tsx';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { PEOPLE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { DIRECTORY_BREADCRUMBS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/directoryBreadcrumbs.tsx';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';
import { PageAbsoluteContainer } from '@dealstack/frontend/shared/layout/PageAbsoluteContainer/PageAbsoluteContainer.tsx';
import { TitleRailToggle } from '@dealstack/frontend/app/pages/Project/Cap/common/TitleRailToggle/TitleRailToggle.tsx';

interface Props {
	selectedEntityLoading?: boolean;
}

const TRANSITION_DURATION = 250;

export const HoldingDetails = ({ selectedEntityLoading }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();
	const location = useLocation();
	const [navigatedFromOwnership] = useState<boolean>(() => {
		return location.state?.navigationSource === true;
	});
	const {
		getHoldingDetailsRequest,
		getHoldingEventsRequest,
		combinedEvents,
		filterDate,
		setFilterDate,
		rail,
		setRail,
		hasNomineeStructure,
		securityName,
	} = useHoldingDetailsController();
	const { selectedEntityId, selectedEntityData } = useContext(CapManageControllerContext);

	const { isAllTransactionValidInDeal, navigateToEntityWithInvalidTransactions, dealId } =
		useContext(DealContext);

	const isLoading = getHoldingDetailsRequest.isLoading || selectedEntityLoading;
	const mipName = getHoldingDetailsRequest.data?.mipName;
	const [presentAsTimeline, setPresentAsTimeline] = useState(false);

	// CAP-1988: the title names the holder, the issuer and the security — all three identify what the
	// page is about, not what is held, so the read carries them on both rails and the title reads the
	// same either way. Only an unresolved read has nothing to interpolate; name the page generically
	// then rather than rendering "'s holding in :".
	const { holderName, issuerName } = getHoldingDetailsRequest.data ?? {};
	const pageTitle =
		holderName && issuerName && securityName
			? tCapManage('holding.details.title', { holderName, issuerName, securityName })
			: tCapManage('holdings.details.title');

	const breadcrumbs = useMemo(
		() => [
			...DIRECTORY_BREADCRUMBS[
				navigatedFromOwnership ? PEOPLE_ROUTES.OWNERSHIP : PEOPLE_ROUTES.HOLDINGS
			]({
				tCapManage,
				dealId: dealId ?? '',
				personId: selectedEntityId,
				personDisplayName: selectedEntityData?.displayName,
			}),
			{
				name: tCapManage('holdings.details.title'),
				url: '',
				noLink: true,
			},
		],
		[tCapManage, dealId, selectedEntityId, selectedEntityData, navigatedFromOwnership]
	);

	useBreadcrumbs(breadcrumbs);

	return (
		<PageAbsoluteContainer>
			<PageHeader pageTitle={pageTitle}>
				<Flex justify="flex-start" align="center" style={{ flex: 1 }}>
					{mipName ? (
						<Badge size="md" fw={400} variant="light" color="blue">
							{mipName}
						</Badge>
					) : null}
				</Flex>
			</PageHeader>

			{isLoading && <InlineLoader />}

			<Flex direction="column" gap="1rem" w="100%" h="100%" style={{ overflow: 'hidden' }}>
				<HoldingDetailsSummary
					data={getHoldingDetailsRequest.data ?? null}
					rail={rail}
					hasNomineeStructure={hasNomineeStructure}
					securityName={securityName}
				></HoldingDetailsSummary>
				<Flex direction="column" gap="0.5rem" style={{ overflow: 'hidden' }}>
					<Flex align="center" justify="space-between">
						<Title order={5} fw={600}>
							{tCapManage('holding.details.events_log')}
						</Title>
						<Flex align="center" gap="0.5rem">
							{hasNomineeStructure && (
								<TitleRailToggle
									key="titleRailToggle"
									rail={rail}
									setRail={setRail}
								/>
							)}
							{!isAllTransactionValidInDeal && (
								<CapInvalidTransactionsWarning
									key="invalidTransactionsWarning"
									showInvalidTransactions={
										navigateToEntityWithInvalidTransactions
									}
								/>
							)}
							<PresentAsSwitch
								key="presentAsSwitch"
								presentAsTimeline={presentAsTimeline}
								setPresentAsTimeline={setPresentAsTimeline}
							/>
							<DateSelectionMenu
								key="dateSelectionMenu"
								dateTo={filterDate}
								setFilterDate={setFilterDate}
								allowDeselect={true}
								defaultDate={filterDate ?? undefined}
								targetIcon="calendar-v3"
								targetIconSize="xl"
								targetFlexDirection="row-reverse"
								targetButtonProps={{
									p: '0.5rem',
									fw: 600,
									variant: 'default',
									bg: 'transparent',
								}}
								buttonDefaultLabel={tCommonUi('date_all_time')}
							/>
						</Flex>
					</Flex>

					<Box h="100%" pb="1rem" style={{ display: 'grid', overflow: 'auto' }}>
						<Transition
							mounted={presentAsTimeline}
							transition="slide-left"
							duration={TRANSITION_DURATION}
							timingFunction="ease"
						>
							{(styles) => (
								<div style={{ ...styles, gridArea: '1 / 1 / 2 / 2' }}>
									<HoldingEventsLogTimeline
										holdingDetails={getHoldingDetailsRequest.data}
										holdingEventsData={getHoldingEventsRequest.data}
									/>
								</div>
							)}
						</Transition>

						<Transition
							mounted={!presentAsTimeline}
							transition="slide-right"
							duration={TRANSITION_DURATION}
							timingFunction="ease"
						>
							{(styles) => (
								<div style={{ ...styles, gridArea: '1 / 1 / 2 / 2' }}>
									<HoldingEventsLogTable holdingEventsData={combinedEvents} />
								</div>
							)}
						</Transition>
					</Box>
				</Flex>
			</Flex>
		</PageAbsoluteContainer>
	);
};
